import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { updateEffectiveDate } from 'api/operations/enrollmentService';

interface EffectiveDateField extends DefaultFormFields {
  effectiveDate: FormControl<string>;
}

export default class ChangeEffectiveDateStore {
  rootStore: RootStore;
  loading = false;
  effectiveDateForm: FormController<EffectiveDateField>;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public async updateEffectiveDate(periodId: string, effectiveDate: string): Promise<void> {
    this.setLoading(true);
    const res = await updateEffectiveDate(periodId, effectiveDate);

    if (res.data?.updateEffectiveDate) {
      this.rootStore.enrollmentCardStore.showToast('Effective date was successfully updated', ToastColorTypes.SUCCESS);
    } else {
      this.rootStore.enrollmentCardStore.showToast(
        'Something went wrong during effective date update',
        ToastColorTypes.WARNING,
      );
    }
    this.setLoading(false);
  }

  // eslint-disable-next-line class-methods-use-this
  private getAllEffectiveDateOptions(effectiveDate: string): OptionType[] {
    const year = effectiveDate.split('-')[0];

    const lastMonth =
      this.rootStore.domainStoreV2.domainHeader.properties?.productsAvailableYear.toString() === year
        ? this.rootStore.domainStoreV2.domainHeader.properties?.productsAvailableMonth || 12
        : 12;

    let monthIterator = 0;
    const options = [];
    while (monthIterator < lastMonth) {
      const month = monthIterator + 1;
      const possibleEffectiveDate = `${year}-${month < 10 ? `0${month}` : month}-01`;
      options.push({
        value: possibleEffectiveDate,
        label: dateFormatters.normDateToLongAmericanDate(possibleEffectiveDate),
      });

      monthIterator += 1;
    }

    return options;
  }

  /**
   * Determines the selectable effective dates for use in the "Edit Effective Date" modal.
   * These dates exclude any dates that are already associated with a period.
   */
  public getAvailableEffectiveDateOptions(unavailableEffectiveDates: string[], effectiveDate: string): OptionType[] {
    return this.getAllEffectiveDateOptions(effectiveDate)
      .map((option) => toJS(option))
      .slice()
      .filter((option) => !unavailableEffectiveDates.includes(option.value));
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.effectiveDateForm = new FormController({
      effectiveDate: new FormControl<string>(''),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
